import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  collapseToRanges,
  prepareCodesForSubmit,
  RangeTooLargeError,
} from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateModifierAdjustmentMutation,
  useGetModifierAdjustmentByIdQuery,
} from "store/queries/modifierAdjustments";

import {
  IProfessionalModifierFormData,
  INITIAL_PROFESSIONAL_MODIFIER_FORM_DATA,
  ProfessionalModifierForm,
} from "./components/ProfessionalModifierForm";

export function CopyProfessionalModifierPage() {
  useDocumentTitle("Copy Professional Modifier Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetModifierAdjustmentByIdQuery(
    id ?? ""
  );
  const [createMutation, { isLoading: isCreating }] =
    useCreateModifierAdjustmentMutation();
  const [formData, setFormData] = useState<IProfessionalModifierFormData>(
    INITIAL_PROFESSIONAL_MODIFIER_FORM_DATA
  );

  // A copy is a fresh create aimed at a new provider/payer, so it seeds as a
  // SCOPED rule: a SCOPED source carries its provider/payer over as a starting
  // point, while TYPICAL/FALLBACK sources have no scope to carry and start
  // empty. CLE-2524: the radio group stays editable here — this only seeds it.
  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    const sourceIsScoped = ruleset.applicability === "SCOPED";
    setFormData({
      modifier: ruleset.modifier,
      multiplier: Number(ruleset.multiplier),
      providerId: sourceIsScoped ? ruleset.providerId : null,
      payerId: sourceIsScoped ? ruleset.payerId : null,
      cptCodes: collapseToRanges(ruleset.cptCodes ?? []),
      groupName: ruleset.groupName ?? "",
      applicability: "SCOPED",
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!formData.modifier.trim()) return;
    if (formData.applicability === "SCOPED" && !formData.providerId) return;
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(formData.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createMutation({
        modifier: formData.modifier.trim().toUpperCase(),
        multiplier: formData.multiplier,
        cptCodes,
        groupName: formData.groupName.trim() || null,
        // CLE-2524: applicability replaces the legacy isDefault flag.
        applicability: formData.applicability,
        providerId:
          formData.applicability === "SCOPED" ? formData.providerId : null,
        payerId: formData.applicability === "SCOPED" ? formData.payerId : null,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Professional modifier rule copied successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to copy professional modifier rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <ProfessionalModifierForm
      title="Copy Professional Modifier Rule"
      submitLabel="Create Copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isCreating}
    />
  );
}
